import { useCallback, useMemo } from 'react';
import { Badge, Card, DataTable } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import AuditLedgerRowDetail from './AuditLedgerRowDetail.jsx';

const RISK_VARIANT = { High: 'danger', Medium: 'warning', Low: 'success' };

/**
 * Audit Ledger table — admin-studio Audit chrome (Card + workspace DataTable).
 * Fits the card width (no horizontal scroll); left/right cell padding balanced.
 */
function AuditLedgerTable({ entries }) {
  const { t } = useTranslation('work-queues-app');

  const renderRowDetail = useCallback((row) => <AuditLedgerRowDetail entry={row} />, []);

  const columns = useMemo(
    () => [
      {
        key: 'id',
        label: t('activity.id'),
        headerClassName: 'w-[7%]',
        cellClassName: 'font-mono text-xs',
      },
      {
        key: 'timestamp',
        label: t('activity.timestamp'),
        headerClassName: 'w-[11%]',
      },
      {
        key: 'actor',
        label: t('activity.actor'),
        headerClassName: 'w-[10%]',
      },
      {
        key: 'action',
        label: t('activity.action'),
        headerClassName: 'w-[14%]',
        cellClassName: 'truncate',
      },
      {
        key: 'studio',
        label: t('filters.studio'),
        headerClassName: 'w-[8%]',
        render: (v) => (
          <Badge variant="secondary" size="small">
            {v}
          </Badge>
        ),
      },
      {
        key: 'artifactId',
        label: t('activity.artifact'),
        headerClassName: 'w-[12%]',
        cellClassName: 'truncate font-mono text-xs',
      },
      {
        key: 'riskTier',
        label: t('activity.riskTier'),
        headerClassName: 'w-[8%]',
        render: (v) => <Badge variant={RISK_VARIANT[v] ?? 'neutral'} size="small">{v}</Badge>,
      },
      {
        key: 'outcome',
        label: t('activity.outcome'),
        headerClassName: 'w-[11%]',
        cellClassName: 'truncate',
      },
      {
        key: 'hash',
        label: t('activity.hash'),
        headerClassName: 'w-[10%]',
        cellClassName: 'font-mono text-xs',
      },
    ],
    [t],
  );

  return (
    <Card className="overflow-hidden">
      <div className="dark:[&_.text-content]:text-white/60">
        <DataTable
          variant="workspace"
          columns={columns}
          data={entries}
          minTableWidth={null}
          emptyMessage={t('activity.noEntries')}
          renderRowDetail={renderRowDetail}
          expandRowAriaLabel={t('activity.expandRow')}
          classNames={{
            // Left matches the preferred inset; extra right pad for hash + chevron.
            th: '!pl-5 !pr-4',
            td: '!pl-5 !pr-4',
            detailCell: '!pl-5 !pr-6 pb-4 pt-0',
          }}
        />
      </div>
    </Card>
  );
}

export default AuditLedgerTable;
